import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Receipt, Pencil, CheckCircle2, RotateCcw } from 'lucide-react';

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [paidFilter, setPaidFilter] = useState('all'); // 'all' | 'unpaid' | 'paid'
  const [vendorFilter, setVendorFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [markingPaidExpense, setMarkingPaidExpense] = useState(null);

  const fetchExpenses = () => {
    const params = paidFilter && paidFilter !== 'all' ? { paid_status: paidFilter } : {};
    api.get('/expenses', { params }).then(r => {
      setExpenses(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidFilter]);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const vendorNames = [...new Set(
    expenses.map((expense) => (expense.vendor || '').trim()).filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));
  const projectNameById = projects.reduce((acc, p) => {
    acc[p.id] = p.name;
    return acc;
  }, {});
  const categories = [...new Set(expenses.map((expense) => expense.category).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredExpenses = expenses.filter((expense) => {
    const expenseDate = String(expense.expense_date || '').slice(0, 10);
    const matchesSearch = !normalizedSearch || [
      expense.description,
      expense.vendor,
      expense.category,
      expense.paid_reference,
      projectNameById[expense.project_id],
      expense.amount,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(normalizedSearch));
    return matchesSearch
      && (vendorFilter === 'all' || (expense.vendor || '').trim() === vendorFilter)
      && (categoryFilter === 'all' || expense.category === categoryFilter)
      && (projectFilter === 'all'
        || (projectFilter === 'overhead' ? !expense.project_id : expense.project_id === projectFilter))
      && (!dateFrom || expenseDate >= dateFrom)
      && (!dateTo || expenseDate <= dateTo);
  });
  const total = filteredExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
  const hasDetailedFilters = vendorFilter !== 'all'
    || categoryFilter !== 'all'
    || projectFilter !== 'all'
    || dateFrom
    || dateTo
    || searchTerm;

  const clearDetailedFilters = () => {
    setVendorFilter('all');
    setCategoryFilter('all');
    setProjectFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const handleMarkUnpaid = async (expense) => {
    try {
      await api.post(`/expenses/${expense.id}/mark-unpaid`);
      toast.success('Expense marked unpaid');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update expense');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="expenses-title">Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">
            {filteredExpenses.length} expense{filteredExpenses.length === 1 ? '' : 's'} · {formatCurrency(total)}
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreate(true)} data-testid="add-global-expense">
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <div className="space-y-1 lg:col-span-2">
              <Label className="text-xs text-slate-600">Search</Label>
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Vendor, description, reference, amount..."
                data-testid="expenses-search-filter"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={paidFilter} onValueChange={setPaidFilter}>
                <SelectTrigger data-testid="expenses-paid-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Vendor</Label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger data-testid="expenses-vendor-filter"><SelectValue placeholder="All vendors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendorNames.map((vendor) => <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="expenses-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>{category.replaceAll('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Project</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger data-testid="expenses-project-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  <SelectItem value="overhead">General overhead</SelectItem>
                  {projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">From</Label>
              <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} data-testid="expenses-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">To</Label>
              <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} data-testid="expenses-date-to" />
            </div>
          </div>
          {hasDetailedFilters && (
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
              <span className="text-slate-500">Showing {filteredExpenses.length} of {expenses.length} loaded expenses</span>
              <Button type="button" size="sm" variant="ghost" onClick={clearDetailedFilters}>Clear detailed filters</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="p-8 text-center">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {hasDetailedFilters && 'No expenses match these filters'}
                {!hasDetailedFilters && paidFilter === 'paid' && 'No paid expenses yet'}
                {!hasDetailedFilters && paidFilter === 'unpaid' && 'No unpaid expenses'}
                {!hasDetailedFilters && paidFilter === 'all' && 'No expenses recorded yet'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map(e => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="text-sm">{formatDate(e.expense_date)}</TableCell>
                    <TableCell className="font-medium">{e.description}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{e.category}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{e.project_id ? (projectNameById[e.project_id] || 'Linked project') : 'General overhead'}</TableCell>
                    <TableCell>{e.vendor || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(e.amount)}</TableCell>
                    <TableCell className="text-right text-slate-500">{formatCurrency(e.tax_amount)}</TableCell>
                    <TableCell>
                      <ExpensePaidBadge expense={e} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!e.paid && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => setMarkingPaidExpense(e)}
                            data-testid={`mark-paid-${e.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Paid
                          </Button>
                        )}
                        {e.paid && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-500 hover:text-slate-700"
                            onClick={() => handleMarkUnpaid(e)}
                            data-testid={`mark-unpaid-${e.id}`}
                            title="Mark as unpaid"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingExpense(e)}
                          data-testid={`edit-expense-${e.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ExpenseDialog
        open={showCreate}
        projects={projects}
        onClose={() => setShowCreate(false)}
        onDone={() => { setShowCreate(false); fetchExpenses(); }}
      />
      <ExpenseDialog
        open={!!editingExpense}
        projects={projects}
        editExpense={editingExpense}
        onClose={() => setEditingExpense(null)}
        onDone={() => { setEditingExpense(null); fetchExpenses(); }}
      />
      <MarkExpensePaidDialog
        open={!!markingPaidExpense}
        expense={markingPaidExpense}
        onClose={() => setMarkingPaidExpense(null)}
        onDone={() => { setMarkingPaidExpense(null); fetchExpenses(); }}
      />
    </div>
  );
}

export function ExpensePaidBadge({ expense }) {
  if (expense?.paid) {
    return (
      <div className="flex flex-col items-start">
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
        </Badge>
        {expense.paid_at && (
          <span className="text-[11px] text-slate-500 mt-0.5">
            {formatDate(expense.paid_at)}
          </span>
        )}
      </div>
    );
  }
  return (
    <Badge variant="outline" className="text-slate-600 border-slate-300">
      Unpaid
    </Badge>
  );
}

export function MarkExpensePaidDialog({ open, expense, onClose, onDone }) {
  const [form, setForm] = useState({ paid_at: '', paid_method: '', paid_reference: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().split('T')[0];
    setForm({
      paid_at: today,
      paid_method: '',
      paid_reference: '',
    });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!expense) return;
    setSaving(true);
    try {
      const payload = {};
      if (form.paid_at) {
        payload.paid_at = new Date(form.paid_at).toISOString();
      }
      if (form.paid_method.trim()) payload.paid_method = form.paid_method.trim();
      if (form.paid_reference.trim()) payload.paid_reference = form.paid_reference.trim();
      await api.post(`/expenses/${expense.id}/mark-paid`, payload);
      toast.success('Expense marked paid');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark Expense Paid</DialogTitle>
        </DialogHeader>
        {expense && (
          <div className="text-sm text-slate-600 -mt-2 mb-1">
            <span className="font-medium text-slate-900">{expense.description}</span>
            {' · '}
            {formatCurrency(expense.amount)}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Paid date</Label>
            <Input
              type="date"
              value={form.paid_at}
              onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              data-testid="mark-paid-date"
            />
            <p className="text-xs text-slate-500">Defaults to today if left blank.</p>
          </div>
          <div className="space-y-2">
            <Label>Paid method (optional)</Label>
            <Input
              value={form.paid_method}
              placeholder="e.g. e-transfer, credit card, cheque"
              onChange={(e) => setForm({ ...form, paid_method: e.target.value })}
              data-testid="mark-paid-method"
            />
          </div>
          <div className="space-y-2">
            <Label>Reference (optional)</Label>
            <Input
              value={form.paid_reference}
              placeholder="Cheque #, confirmation #, etc."
              onChange={(e) => setForm({ ...form, paid_reference: e.target.value })}
              data-testid="mark-paid-reference"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-wrap">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
              data-testid="confirm-mark-paid"
            >
              {saving ? 'Saving...' : 'Mark Paid'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDialog({ open, onClose, onDone, projects = [], editExpense = null }) {
  const [form, setForm] = useState({
    category: 'other',
    project_id: '',
    description: '',
    amount: '',
    tax_amount: '0',
    vendor: '',
    expense_date: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editExpense) {
      setForm({
        category: editExpense.category || 'other',
        project_id: editExpense.project_id || '',
        description: editExpense.description || '',
        amount: String(editExpense.amount || ''),
        tax_amount: String(editExpense.tax_amount || '0'),
        vendor: editExpense.vendor || '',
        expense_date: editExpense.expense_date ? new Date(editExpense.expense_date).toISOString().split('T')[0] : '',
        notes: editExpense.notes || '',
      });
    } else {
      setForm({
        category: 'other',
        project_id: '',
        description: '',
        amount: '',
        tax_amount: '0',
        vendor: '',
        expense_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
    }
  }, [open, editExpense]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) { toast.error('Description and amount required'); return; }
    if (form.category === 'subcontractor' && !form.project_id) {
      toast.error('Project is required for subcontractor expenses');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_id: form.project_id || null,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount),
        tax_amount: parseFloat(form.tax_amount) || 0,
        vendor: form.vendor,
        expense_date: form.expense_date || null,
        notes: form.notes,
      };
      if (editExpense) {
        await api.put(`/expenses/${editExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', payload);
        toast.success('Expense added');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader><DialogTitle>{editExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 w-full min-w-0">
          <div className="space-y-2 min-w-0">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="materials">Materials</SelectItem>
                <SelectItem value="labor">Labor</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`space-y-2 min-w-0 ${form.category === 'subcontractor' ? 'p-3 rounded-md border border-amber-200 bg-amber-50' : ''}`}>
            <Label>{form.category === 'subcontractor' ? 'Project * (required for subcontractor)' : 'Project (optional)'}</Label>
            <Select value={form.project_id || 'none'} onValueChange={v => setForm({...form, project_id: v === 'none' ? '' : v})}>
              <SelectTrigger className="w-full min-w-0"><SelectValue placeholder="Assign to a project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General overhead (no project)</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 min-w-0">
            <Label>Description *</Label>
            <Input className="max-w-full" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
            <div className="space-y-2 min-w-0"><Label>Amount *</Label><Input className="max-w-full" type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} /></div>
            <div className="space-y-2 min-w-0"><Label>Tax Amount</Label><Input className="max-w-full" type="number" step="0.01" value={form.tax_amount} onChange={e => setForm({...form, tax_amount: e.target.value})} /></div>
          </div>
          <div className="space-y-2 min-w-0">
            <Label>Expense Date</Label>
            <Input className="max-w-full" type="date" value={form.expense_date} onChange={e => setForm({...form, expense_date: e.target.value})} />
          </div>
          <div className="space-y-2 min-w-0">
            <Label>Vendor</Label>
            <Input className="max-w-full" value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} />
          </div>
          <div className="space-y-2 min-w-0">
            <Label>Notes</Label>
            <Input className="max-w-full" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-wrap min-w-0">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
              {saving ? (editExpense ? 'Saving...' : 'Adding...') : (editExpense ? 'Save Changes' : 'Add Expense')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
